import {Button, Card, CardContent, CircularProgress, Grid, TextField, Typography} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import {ChangeEvent, useEffect, useState} from "react";
import {Title, useNotify} from "react-admin";
import {client} from "../../dataProvider";

type EtaParamName =
  | "TIME_COEFF"
  | "ONE_STEP_TIME"
  | "MULTI_STEP_TIME"
  | "WEIGHT_COEFF"
  | "PACKAGE_COEFF"
  | "VOLUME_COEFF"
    | "CASHET_COEFF";

type EtaParams = Record<EtaParamName, number>;

type EtaConfig = {
  config_type?: string;
  params: EtaParams;
};

const fields: Array<{name: EtaParamName; label: string; helperText: string}> = [
  {name: "TIME_COEFF", label: "Coefficiente tempo percorrenza", helperText: "Moltiplicatore applicato alla durata della percorrenza. Valore 1 = tempo invariato, valori maggiori aumentano il peso della percorrenza."},
  {name: "ONE_STEP_TIME", label: "Tempo per fermata (Espresso in secondi)", helperText: "Tempo aggiunto per ogni fermata effettuata. Utile per rappresentare il costo operativo base di ogni stop."},
  {name: "MULTI_STEP_TIME", label: "Tempo extra per consegne multiple (Espresso in secondi)", helperText: "Tempo aggiuntivo applicato alle consegne ulteriori rispetto alla singola fermata. Utile per dare valore alle fermate con più missioni."},
  {name: "WEIGHT_COEFF", label: "Coefficiente peso", helperText: "Incremento di tempo legato al peso totale della spedizione. Può essere usato per modellare carichi più impegnativi da gestire. Se 0 non aggiunge tempo per la gestione del peso movimentato."},
  {name: "PACKAGE_COEFF", label: "Coefficiente colli", helperText: "Tempo aggiuntivo in base al numero di colli movimentati. Utile quando la quantità di pacchi incide sulle operazioni di carico e scarico. Se 0 non aggiunge tempo per la gestione dei colli movimentati."},
  {name: "VOLUME_COEFF", label: "Coefficiente volume", helperText: "Incremento di tempo collegato al volume complessivo trasportato. Utile per tener conto dell’ingombro delle merci. Se 0 non aggiunge tempo per la gestione del volume movimentato. "},
    {name: "CASHET_COEFF", label: "Coefficiente contrassegno", helperText: "Aggiungi i secondi indicati in caso di contrassegno. Se 0 non aggiunge tempo per la gestione del contrassegno."},
];

export const EtaConfigPage = () => {
  const notify = useNotify();
  const [config, setConfig] = useState<EtaConfig | null>(null);
  const [params, setParams] = useState<EtaParams | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client("configs/eta", {method: "GET"})
      .then((response: EtaConfig) => {
        setConfig(response);
        setParams(response.params);
      })
      .catch(() => notify("Impossibile caricare la configurazione ETA.", "error"))
      .finally(() => setLoading(false));
  }, [notify]);

  const handleChange = (name: EtaParamName) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setParams((current) => current ? {...current, [name]: value === "" ? 0 : Number(value)} : current);
  };

  const handleSave = () => {
    if (!config || !params || fields.some(({name}) => !Number.isFinite(params[name]))) {
      notify("Inserisci un valore numerico valido per tutti i parametri.", "error");
      return;
    }

    if (params.TIME_COEFF < 0) {
      notify("Il coefficiente tempo deve essere maggiore o uguale a zero.", "error");
      return;
    }

    setSaving(true);
    client("configs/eta", {
      method: "POST",
      data: {config_type: config.config_type || "eta", params},
    })
      .then((response: EtaConfig) => {
        setConfig(response);
        setParams(response.params);
        notify("Configurazione ETA salvata.", "success");
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        notify(detail || "Impossibile salvare la configurazione ETA.", "error");
      })
      .finally(() => setSaving(false));
  };

  return (
    <Card>
      <Title title="Configurazione ETA" />
      <CardContent>
        <Typography variant="h5" gutterBottom>Configurazione ETA</Typography>
        <Typography color="textSecondary" paragraph>
          Imposta i parametri usati per calcolare l’orario di arrivo stimato.
        </Typography>
        {loading || !params ? <CircularProgress /> : (
          <Grid container spacing={2}>
            {fields.map(({name, label, helperText}) => (
              <Grid item xs={12} md={6} key={name}>
                <TextField
                  fullWidth
                  type="number"
                  label={label}
                  helperText={helperText}
                  value={params[name]}
                  onChange={handleChange(name)}
                  inputProps={{step: "any", min: name === "TIME_COEFF" ? 0 : undefined}}
                  variant="outlined"
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                Salva configurazione
              </Button>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
